import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import Select from 'react-select';
import { itemToOptions } from '../components/utils/GlobalFunctions';

const Categories = ({ subentities, entities, metrics, scope3Categories, branding }) => {
  const { headers } = branding.fonts?.[0].font;
  const { body } = branding.fonts?.[0].font;
  const [entity, setEntity] = useState(null);
  const [subentity, setSubentity] = useState(null);
  const [metric, setMetric] = useState(null);

  // Filter out subentities with hideInUI == true
  const visibleSubentities = subentities?.filter((subentity) => !subentity.hideInUI) || [];

  const options = useMemo(() => {
    // Filter subentities based on selected entity
    let filteredSubentities = visibleSubentities;
    if (entity) {
      filteredSubentities = filteredSubentities?.filter(
        (subentity) => subentity.entity && subentity.entity.includes(entity)
      );
    }

    // Filter metrics based on selected subentity
    let filteredMetrics = metrics?.filter((metric) => metric.showInCategories);
    if (subentity) {
      const selectedSubentity = filteredSubentities?.find((s) => s.id === subentity);
      if (selectedSubentity && selectedSubentity.metric) {
        filteredMetrics = filteredMetrics?.filter((metric) => selectedSubentity.metric.includes(metric.id));
      }
    }

    return {
      entities: _.sortBy(entities?.map(itemToOptions), ['label']),
      subentities: _.sortBy(filteredSubentities?.map(itemToOptions), ['label']),
      metrics: _.sortBy(filteredMetrics?.map(itemToOptions), ['label']),
    };
  }, [entity, subentity, entities, subentities, metrics]);

  // Create table data by looping through subentities
  const tableData = [];

  visibleSubentities.forEach((subentity) => {
    // Get the entities that this subentity belongs to
    const subentityEntities = subentity.entity || [];

    // Get the metrics for this subentity
    const subentityMetrics = subentity.metric || [];

    // Create a row for each entity that this subentity has
    subentityEntities.forEach((entityId) => {
      const entity = entities?.find((e) => e.id === entityId);
      if (entity) {
        // Get the scope 3 category for this entity
        const scope3Category = scope3Categories?.find((cat) => cat.id === entity.scope_3_cat);

        // Get the metric names for this subentity
        const metricNames = subentityMetrics
          .map((metricId) => {
            const metric = metrics?.find((m) => m.id === metricId && m.showInCategories);
            return metric?.name || '';
          })
          ?.filter((m) => m !== '')
          .join(', ');

        tableData.push({
          entityName: entity.name || 'Unknown Entity',
          entityId: entity.id,
          subentityName: subentity.name || 'Unknown Subentity',
          subentityId: subentity.id,
          metrics: metricNames || 'No metrics',
          scope3Category: scope3Category?.name || 'No Scope 3 Category',
        });
      }
    });
  });

  // Sort the data by entity name, then by subentity name
  const sortedTableData = _.sortBy(tableData, ['entityName', 'subentityName']);

  // Filter the data based on user selections
  const filteredTableData = sortedTableData.filter((row) => {
    // Filter by entity if selected
    if (entity && row.entityId !== entity) {
      return false;
    }

    // Filter by subentity if selected
    if (subentity && row.subentityId !== subentity) {
      return false;
    }

    // Filter by metric if selected
    if (metric) {
      const rowMetrics = row.metrics.split(', ').map((m) => m.trim());
      const selectedMetricName = options.metrics?.find((m) => m.value === metric)?.label;
      if (!rowMetrics.includes(selectedMetricName)) {
        return false;
      }
    }

    return true;
  });

  const thStyle = {
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    color: 'black',
    fontWeight: 'bold',
  };

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <h2 style={{ fontFamily: headers, marginTop: 0 }}>Usage Categories</h2>
      <span style={{ marginBottom: 4 }}>
        This table shows the relationships between usage types, usage sub-types, units, and scope 3 categories.
      </span>

      <div style={{ marginTop: 16 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            backgroundColor: 'white',
            justifyContent: 'center',
            marginBottom: 8,
            borderRadius: 8,
            border: '1px solid #ddd',
            padding: 12,
            gap: 16,
            flexWrap: 'wrap',
          }}>
          <div style={{ flexGrow: 1, minWidth: 200 }}>
            <label>Usage Type</label>
            <Select
              id={'Categories-entity'}
              placeholder='Select Usage Type'
              isClearable={true}
              name={'entity'}
              options={options.entities}
              onChange={(selected) => {
                setEntity(selected?.value);
                // Clear dependent filters when entity changes
                setSubentity(null);
                setMetric(null);
              }}
              value={options.entities?.find((obj) => obj.value == entity) || null}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 7,
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 7,
                }),
              }}
              menuPortalTarget={document.body}
            />
          </div>
          <div style={{ flexGrow: 1, minWidth: 200 }}>
            <label>Usage Sub-Type</label>
            <Select
              id={'Categories-subentity'}
              placeholder='Select Usage Sub-Type'
              isClearable={true}
              name={'subentity'}
              options={options.subentities}
              onChange={(selected) => {
                setSubentity(selected?.value);
                // Clear dependent filters when subentity changes
                setMetric(null);
              }}
              value={options.subentities?.find((obj) => obj.value == subentity) || null}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 7,
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 7,
                }),
              }}
              menuPortalTarget={document.body}
            />
          </div>
          <div style={{ flexGrow: 1, minWidth: 200 }}>
            <label>Unit</label>
            <Select
              id={'Categories-metric'}
              placeholder='Select Unit'
              isClearable={true}
              name={'metric'}
              options={options.metrics}
              onChange={(selected) => {
                setMetric(selected?.value);
              }}
              value={options.metrics?.find((obj) => obj.value == metric) || null}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 7,
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 7,
                }),
              }}
              menuPortalTarget={document.body}
            />
          </div>
        </div>
        {sortedTableData.length != 0 && filteredTableData.length === 0 ? (
          <div
            style={{
              fontSize: '15px',
              fontFamily: body,
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              backgroundColor: '#fff',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #ddd',
            }}>
            <i className='fa-solid fa-triangle-exclamation' style={{ color: '#000', fontSize: '18px' }} />
            No search results found.
          </div>
        ) : (
          <div
            style={{
              overflowX: 'auto',
              borderRadius: 8,
              border: '1px solid #ddd',
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto',
            }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                borderRadius: '8px',
                overflow: 'hidden',
                minWidth: 650,
                overflowX: 'auto',
              }}>
              <thead>
                <tr
                  style={{
                    backgroundColor: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                  }}>
                  <th style={thStyle}>Usage Type</th>
                  <th style={thStyle}>Usage Sub-Type</th>
                  <th style={thStyle}>Units</th>
                  <th style={thStyle}>Scope 3 Category</th>
                </tr>
              </thead>
              <tbody>
                {!subentities?.length > 0 ||
                !entities?.length > 0 ||
                !metrics?.length > 0 ||
                !scope3Categories?.length > 0 ? (
                  <tr>
                    <td
                      colSpan='4'
                      style={{
                        textAlign: 'center',
                        padding: '20px',
                        fontSize: '15px',
                        fontFamily: body,
                        backgroundColor: '#fff',
                        fontWeight: 'bold',
                      }}>
                      Categories Loading...
                    </td>
                  </tr>
                ) : (
                  filteredTableData.map((row, index) => (
                    <tr
                      key={`${row.entityId}-${row.subentityId}-${index}`}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                      }}>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: index === filteredTableData.length - 1 ? 'none' : '1px solid #ddd',
                          verticalAlign: 'top',
                        }}>
                        <div style={{ fontWeight: 'medium', marginBottom: '4px' }}>{row.entityName}</div>
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: index === filteredTableData.length - 1 ? 'none' : '1px solid #ddd',
                          verticalAlign: 'top',
                        }}>
                        <div style={{ fontWeight: 'medium', marginBottom: '4px' }}>{row.subentityName}</div>
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: index === filteredTableData.length - 1 ? 'none' : '1px solid #ddd',
                          verticalAlign: 'top',
                        }}>
                        <div>{row.metrics}</div>
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: index === filteredTableData.length - 1 ? 'none' : '1px solid #ddd',
                          verticalAlign: 'top',
                        }}>
                        <div>{row.scope3Category}</div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        <span>Total rows: {filteredTableData.length}</span>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    subentities: state.subentities,
    entities: state.entities,
    metrics: state.metric?.list,
    scope3Categories: state.metric?.scope3Categories,
    branding: state.branding,
  };
};

export default connect(mapStateToProps)(Categories);
